import DescBlock from '@/layouts/game-settings/components/DescBlock'
import Block from '@/layouts/game-settings/components/Block'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { FieldGroup, SuperAdminInfo } from '@/ui'
import { useCurrentUser } from '@/api/useCurrentUser'
import { FormErrorMessage, Textarea, Toggle } from '@dashboard/ui'
import { GameSettingsRead } from '@/api/dashboard'
import { ErrorMessage } from '@hookform/error-message'
import { deepLinkRegexp } from '@/libs'
import { InputVars } from '@/components/InputVars'

export const CheckoutSettings = () => {
  const { t } = useTranslation()
  const { canEdit } = useCurrentUser()
  const { isSuperAdmin } = useCurrentUser()

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<GameSettingsRead>()

  return (
    <DescBlock
      title={t('game-settings.checkout-settings.title')}
      description={t('game-settings.checkout-settings.desc')}
    >
      <Block>
        <FieldGroup label={t('game-settings.checkout-settings.purchase-successful')}>
          <Textarea {...register('purchase_successful_text')} maxLength={255} disabled={!canEdit} />
        </FieldGroup>

        <FieldGroup label={t('game-settings.checkout-settings.receipt-appreciation')}>
          <Textarea {...register('receipt_appreciation_text')} maxLength={255} disabled={!canEdit} />
        </FieldGroup>

        <FieldGroup label={t('game-settings.checkout-settings.back-to-game-url')}>
          <Block className="rounded-md border-border-secondary bg-fg-secondary p-3 shadow-none">
            <div className="mb-2 text-xs font-medium uppercase text-text-quarterary-hover">
              {t('game-settings.checkout-settings.apple')}
            </div>
            <Controller
              name="back_to_game_settings.ios_custom_url_scheme"
              control={control}
              rules={{
                required: false,
                pattern: { value: deepLinkRegexp, message: t('validation.invalid_url') },
              }}
              render={({ field }) => (
                <>
                  <InputVars
                    disabled={!canEdit}
                    value={field.value || ''}
                    onChange={v => {
                      field.onChange(v)
                    }}
                    maxLength={255}
                    label={<span>{t('game-settings.checkout-settings.ios_custom_url_scheme')}</span>}
                    items={[{ value: '{order_id}', name: 'Order ID' }]}
                    placeholder="game://"
                  />
                  <ErrorMessage
                    name="back_to_game_settings.ios_custom_url_scheme"
                    errors={errors}
                    render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
                  />
                </>
              )}
            />

            <Controller
              name="back_to_game_settings.ios_universal_link"
              control={control}
              rules={{
                required: false,
                pattern: { value: deepLinkRegexp, message: t('validation.invalid_url') },
              }}
              render={({ field }) => (
                <>
                  <InputVars
                    className="mt-3"
                    disabled={!canEdit}
                    value={field.value || ''}
                    onChange={v => {
                      field.onChange(v)
                    }}
                    maxLength={255}
                    label={<span>{t('game-settings.checkout-settings.ios_universal_link')}</span>}
                    items={[{ value: '{order_id}', name: 'Order ID' }]}
                    placeholder="https://game.link"
                  />
                  <ErrorMessage
                    name="back_to_game_settings.ios_universal_link"
                    errors={errors}
                    render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
                  />
                </>
              )}
            />

            <div className="mb-2 mt-3 border-t border-border-secondary pt-2 text-xs font-medium uppercase text-text-quarterary-hover">
              {t('game-settings.checkout-settings.android')}
            </div>
            <Controller
              name="back_to_game_settings.android_app_link"
              control={control}
              rules={{
                required: false,
                pattern: { value: deepLinkRegexp, message: t('validation.invalid_url') },
              }}
              render={({ field }) => (
                <>
                  <InputVars
                    disabled={!canEdit}
                    value={field.value || ''}
                    onChange={v => {
                      field.onChange(v)
                    }}
                    maxLength={255}
                    label={<span>{t('game-settings.checkout-settings.android_app_link')}</span>}
                    items={[{ value: '{order_id}', name: 'Order ID' }]}
                    placeholder="game:// or https://game.link"
                  />
                  <ErrorMessage
                    name="back_to_game_settings.android_app_link"
                    errors={errors}
                    render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
                  />
                </>
              )}
            />
          </Block>
        </FieldGroup>

        {isSuperAdmin && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex items-center gap-2 text-text-secondary">
              Show hub popup after purchase <SuperAdminInfo />
            </div>
            <Controller
              control={control}
              name="enable_purchase_popup"
              render={({ field }) => (
                <Toggle
                  className="ml-auto"
                  size="sm"
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  disabled={!canEdit}
                />
              )}
            />
          </div>
        )}
      </Block>
    </DescBlock>
  )
}
